/**
 * The board the relief is cast on, and the paper it rests on.
 *
 * Both replace tricks. The board was a 0.006-deep extrusion painted with
 * `MeshBasicMaterial` — unlit, and four per cent of the spike height, so it never
 * appeared in a finished picture at all. The paper did not exist: the ground was
 * `setClearColor`, and the object's contact with it was three flat stretched
 * silhouettes of the coastline, blended at fixed opacity, which could not respond
 * to the light because nothing about them knew where the light was.
 *
 * Now the board is a real slab with a real wall, the paper is a real surface that
 * receives a real shadow map, and the two touch: the board's underside sits exactly
 * on the paper's plane. Grounding is most of what makes a rendering read as a
 * photograph, and a stretched silhouette does the opposite.
 *
 * The wall runs from the relief's own surface at the coastline down to the
 * underside, in one piece. There is no separate skirt and no top face: the relief
 * covers the interior, the wall stands exactly on the boundary polygon, and the
 * two meet along it. Because the height the wall starts from is read at the mip
 * level the relief is currently displaced by, the seam is exact rather than
 * approximately closed.
 */

import * as THREE from "three";

import {
  PAPER,
  PAPER_ROUGHNESS,
  PAPER_SHEEN,
  PLATE_ALBEDO,
  PLATE_CLEARCOAT,
  PLATE_CLEARCOAT_ROUGHNESS,
  PLATE_DEPTH,
  PLATE_FILLET,
  PLATE_ROUGHNESS,
} from "../../shared/constants.ts";
import { oklchToGamut } from "../../shared/oklab.ts";
import type { MipLevel, ReliefFields, Ring } from "../../shared/types.ts";

/** Wide enough to fill the frame at every framing the camera can reach. */
const TABLE_SPAN = 40;

/** Underside of the board, and therefore the plane of the paper. */
export const PLATE_BOTTOM = -PLATE_DEPTH;

/**
 * Rows of the wall, as (fraction of the fillet below the rim, how far the normal
 * has rotated from up to outward).
 *
 * The fillet is a normal rotation rather than an offset in position. At this scale
 * — a couple of thousandths of the country's width, three or four pixels — what a
 * real fillet contributes is the highlight rolling over the rim, not a change in
 * silhouette. Offsetting the polygon would buy nothing visible and would risk
 * self-intersection wherever a strait is narrower than the offset.
 */
const RIM_ROWS: ReadonlyArray<readonly [drop: number, outward: number]> = [
  [0, 0.3],
  [0.5, 0.78],
  [1, 1],
];

/**
 * Height of the relief surface at an arbitrary point, which is what the top of
 * the wall has to agree with. Before any relief exists the outline stage passes a
 * flat one, so the board can appear on the paper the moment the boundary is known.
 */
export type HeightAt = (x: number, y: number) => number;

/**
 * Sampler reading a mip level exactly as the shader's linear filter would, so the
 * wall meets the relief rather than nearly meeting it.
 *
 * The level matters. Read level zero while the relief is displaced by level two
 * and every coastal city opens a gap the height of its own spire, because a
 * max-preserving reduction is precisely designed to disagree with a point sample.
 */
export function mipSampler(fields: ReliefFields, level: number): HeightAt {
  const mip = fields.heightMips[Math.min(level, fields.heightMips.length - 1)];
  return (x, y) => sampleHeight(mip, fields.extent, x, y);
}

export function plateMaterial(): THREE.MeshPhysicalMaterial {
  const albedo = oklchToGamut(PLATE_ALBEDO);
  const material = new THREE.MeshPhysicalMaterial({
    roughness: PLATE_ROUGHNESS,
    metalness: 0,
    clearcoat: PLATE_CLEARCOAT,
    clearcoatRoughness: PLATE_CLEARCOAT_ROUGHNESS,
    // The far wall's inner face would otherwise be a hole at grazing angles, and
    // the object is meant to be solid.
    side: THREE.DoubleSide,
    shadowSide: THREE.FrontSide,
  });
  material.color.setRGB(albedo[0], albedo[1], albedo[2], THREE.LinearSRGBColorSpace);
  return material;
}

export function tableMesh(): THREE.Mesh {
  const albedo = oklchToGamut(PAPER);
  const material = new THREE.MeshPhysicalMaterial({
    roughness: PAPER_ROUGHNESS,
    metalness: 0,
    // Paper's fibres retroreflect; sheen is what keeps it from reading as plaster.
    sheen: PAPER_SHEEN,
    sheenRoughness: 0.9,
    side: THREE.FrontSide,
  });
  material.color.setRGB(albedo[0], albedo[1], albedo[2], THREE.LinearSRGBColorSpace);
  material.sheenColor.setRGB(0.6, 0.58, 0.55, THREE.LinearSRGBColorSpace);

  const mesh = new THREE.Mesh(new THREE.PlaneGeometry(TABLE_SPAN, TABLE_SPAN), material);
  mesh.position.z = PLATE_BOTTOM;
  mesh.receiveShadow = true;
  return mesh;
}

/**
 * A flat top face, for the outline stage only.
 *
 * The finished render needs no lid: the relief covers the interior and the wall
 * stands on the boundary polygon, so the two meet along it. But until the relief
 * arrives the board would be an open box with the paper visible through it.
 */
export function lidGeometry(rings: ReadonlyArray<Ring>): THREE.BufferGeometry {
  const shapes: THREE.Shape[] = [];
  for (const ring of rings) {
    const points = ringPoints(ring);
    if (points < 3) continue;
    const shape = new THREE.Shape();
    shape.moveTo(ring[0], ring[1]);
    for (let point = 1; point < points; point += 1) {
      shape.lineTo(ring[point * 2], ring[point * 2 + 1]);
    }
    shapes.push(shape);
  }
  return new THREE.ShapeGeometry(shapes);
}

/**
 * The rings the framing should measure.
 *
 * Italy's ADM0 boundary includes Lampedusa and Pantelleria, two hundred kilometres
 * south of Sicily. Fitting the camera to every ring spends a third of the poster on
 * open sea in order to keep a twenty-square-kilometre island in shot, which is why
 * the country looked marooned however good the fit itself was.
 *
 * Rings below a thousandth of the largest one's area drop out of the *fit* only.
 * They are still built, still lit and still rendered; they may simply fall outside
 * the frame, which for an islet that far offshore is the right trade. The scale is
 * relative to the largest ring rather than absolute, so an archipelago whose
 * islands *are* the country keeps them all.
 */
export function framingRings(rings: ReadonlyArray<Ring>): Ring[] {
  const measured = rings
    .map((ring) => ({ ring, area: Math.abs(signedArea(ring, ringPoints(ring))) }))
    .filter((entry) => entry.area > 0);
  if (measured.length === 0) return [...rings];
  let largest = 0;
  for (const entry of measured) largest = Math.max(largest, entry.area);
  const cut = largest * 0.001;
  const kept = measured.filter((entry) => entry.area >= cut).map((entry) => entry.ring);
  return kept.length > 0 ? kept : [...rings];
}

/** The wall, from the relief's surface at the coastline down to the underside. */
export function rimGeometry(
  rings: ReadonlyArray<Ring>,
  heightAt: HeightAt,
): THREE.BufferGeometry {
  const rows = RIM_ROWS.length + 1;

  let corners = 0;
  for (const ring of rings) {
    const points = ringPoints(ring);
    if (points >= 3) corners += points;
  }

  const positions = new Float32Array(corners * rows * 3);
  const normals = new Float32Array(corners * rows * 3);
  const indices = new Uint32Array(corners * (rows - 1) * 6);
  let vertex = 0;
  let index = 0;

  for (const ring of rings) {
    const points = ringPoints(ring);
    if (points < 3) continue;
    // Outward is only well defined once the winding is known, and geoBoundaries
    // does not promise one.
    const clockwise = signedArea(ring, points) < 0;
    const first = vertex;

    for (let point = 0; point < points; point += 1) {
      const x = ring[point * 2];
      const y = ring[point * 2 + 1];
      const outward = vertexNormal(ring, points, point, clockwise);
      const top = heightAt(x, y);

      for (let row = 0; row < rows; row += 1) {
        const target = vertex * 3;
        positions[target] = x;
        positions[target + 1] = y;
        if (row < RIM_ROWS.length) {
          const [drop, blend] = RIM_ROWS[row];
          positions[target + 2] = top - drop * PLATE_FILLET;
          const up = 1 - blend;
          const length = Math.hypot(outward[0] * blend, outward[1] * blend, up);
          normals[target] = (outward[0] * blend) / length;
          normals[target + 1] = (outward[1] * blend) / length;
          normals[target + 2] = up / length;
        } else {
          positions[target + 2] = PLATE_BOTTOM;
          normals[target] = outward[0];
          normals[target + 1] = outward[1];
          normals[target + 2] = 0;
        }
        vertex += 1;
      }
    }

    for (let point = 0; point < points; point += 1) {
      const here = first + point * rows;
      const next = first + ((point + 1) % points) * rows;
      for (let row = 0; row < rows - 1; row += 1) {
        // Wound so the outward face is the front one for a counter-clockwise ring,
        // which is what `shadowSide` depends on.
        indices[index] = here + row;
        indices[index + 1] = here + row + 1;
        indices[index + 2] = next + row + 1;
        indices[index + 3] = here + row;
        indices[index + 4] = next + row + 1;
        indices[index + 5] = next + row;
        index += 6;
      }
    }
  }

  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute("position", new THREE.BufferAttribute(positions, 3));
  geometry.setAttribute("normal", new THREE.BufferAttribute(normals, 3));
  geometry.setIndex(new THREE.BufferAttribute(indices, 1));
  return geometry;
}

/** Ring points without the repeated closing vertex, which every ring may carry. */
function ringPoints(ring: Ring): number {
  const points = ring.length / 2;
  if (points < 2) return 0;
  const closed = ring[0] === ring[ring.length - 2] && ring[1] === ring[ring.length - 1];
  return closed ? points - 1 : points;
}

function signedArea(ring: Ring, points: number): number {
  let total = 0;
  for (let point = 0, previous = points - 1; point < points; previous = point++) {
    total +=
      ring[previous * 2] * ring[point * 2 + 1] - ring[point * 2] * ring[previous * 2 + 1];
  }
  return total / 2;
}

/** Mean of the two adjacent edge normals, so the wall reads as smooth. */
function vertexNormal(
  ring: Ring,
  points: number,
  point: number,
  clockwise: boolean,
): [number, number] {
  const previous = (point + points - 1) % points;
  const next = (point + 1) % points;
  const sign = clockwise ? -1 : 1;
  let x = 0;
  let y = 0;
  for (const [from, to] of [
    [previous, point],
    [point, next],
  ]) {
    const edgeX = ring[to * 2] - ring[from * 2];
    const edgeY = ring[to * 2 + 1] - ring[from * 2 + 1];
    const length = Math.hypot(edgeX, edgeY);
    if (length === 0) continue;
    // Outward of a counter-clockwise edge is its tangent turned clockwise.
    x += (sign * edgeY) / length;
    y += (-sign * edgeX) / length;
  }
  const length = Math.hypot(x, y);
  return length > 0 ? [x / length, y / length] : [1, 0];
}

/**
 * Bilinear read of a mip level, matching what the shader's linear filter returns
 * at the same point. Half-texel offsets included: the seam depends on it.
 */
function sampleHeight(
  mip: MipLevel,
  extent: readonly [number, number, number, number],
  x: number,
  y: number,
): number {
  const columns = mip.width;
  const rows = mip.height;
  const u = ((x - extent[0]) / (extent[1] - extent[0])) * columns - 0.5;
  const v = ((extent[3] - y) / (extent[3] - extent[2])) * rows - 0.5;
  const column = Math.floor(u);
  const row = Math.floor(v);
  const fractionX = u - column;
  const fractionY = v - row;
  const left = clampIndex(column, columns);
  const right = clampIndex(column + 1, columns);
  const top = clampIndex(row, rows) * columns;
  const bottom = clampIndex(row + 1, rows) * columns;
  const upper =
    mip.data[top + left] + (mip.data[top + right] - mip.data[top + left]) * fractionX;
  const lower =
    mip.data[bottom + left] + (mip.data[bottom + right] - mip.data[bottom + left]) * fractionX;
  return upper + (lower - upper) * fractionY;
}

function clampIndex(value: number, limit: number): number {
  return value < 0 ? 0 : value > limit - 1 ? limit - 1 : value;
}
